import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Copy,
  Check,
  X,
  MessageCircle,
  Send,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  chatIntegrationAPI,
  type ChatWidget,
} from '@/services/chatIntegration';
import { useQueryClient } from '@tanstack/react-query';

// Position mapping: UI -> Backend
const POSITION_MAP: Record<string, string> = {
  'bottom-right': 'BR',
  'top-right': 'TR',
  'bottom-left': 'BL',
  'top-left': 'TL',
};

const POSITION_REVERSE_MAP: Record<string, string> = {
  BR: 'bottom-right',
  TR: 'top-right',
  BL: 'bottom-left',
  TL: 'top-left',
};

export function ChatIntegrationDetails() {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { widgetId } = useParams<{ widgetId: string }>();
  const mode: 'add' | 'edit' = widgetId ? 'edit' : 'add';

  // Load widget data in edit mode
  const { data: existingWidget, isLoading: isLoadingWidget } =
    useQuery<ChatWidget>({
      queryKey: ['chatWidget', widgetId],
      queryFn: () => chatIntegrationAPI.getChatWidgetById(widgetId!),
      enabled: mode === 'edit' && !!widgetId,
    });

  const [widgetSettings, setWidgetSettings] = useState({
    primaryColor: '#030213',
    textColor: '#ffffff',
    position: 'bottom-right',
    welcomeMessage: '',
    companyName: '',
    websiteUrl: '',
    showTypingIndicator: false,
    enableSoundNotifications: false,
    offlineMessage: '',
    placeholderText: '',
    headerText: '',
    buttonText: '',
    autoOpen: false,
    autoDelay: false,
    borderRadius: '8',
    size: 'medium',
  });

  // Load widget data into form when editing
  useEffect(() => {
    if (mode === 'edit' && existingWidget) {
      setWidgetSettings({
        primaryColor: existingWidget.color || '#030213',
        textColor: existingWidget.textColor || '#ffffff',
        position:
          POSITION_REVERSE_MAP[existingWidget.position || 'BR'] ||
          'bottom-right',
        welcomeMessage: existingWidget.welcomeMessage || '',
        companyName: existingWidget.companyName || '',
        websiteUrl: existingWidget.websiteUrl || '',
        showTypingIndicator: existingWidget.typingIndicator || false,
        enableSoundNotifications: existingWidget.soundNotifications || false,
        offlineMessage: existingWidget.offlineMessage || '',
        placeholderText: existingWidget.inputPlaceholder || '',
        headerText: existingWidget.headerText || '',
        buttonText: existingWidget.buttonText || '',
        autoOpen: existingWidget.autoOpen || false,
        autoDelay: existingWidget.autoDelay || false,
        borderRadius: existingWidget.borderRadius
          ? existingWidget.borderRadius.replace('px', '')
          : '8',
        size: existingWidget.widgetSize || 'medium',
      });
    }
  }, [mode, existingWidget]);

  // Live preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMessages, setPreviewMessages] = useState([
    {
      type: 'bot',
      text: widgetSettings.welcomeMessage || 'Hi! How can we help you today?',
      time: 'Just now',
    },
  ]);
  const [previewInput, setPreviewInput] = useState('');

  // Update preview messages when welcome message changes
  useEffect(() => {
    if (widgetSettings.welcomeMessage) {
      setPreviewMessages([
        { type: 'bot', text: widgetSettings.welcomeMessage, time: 'Just now' },
      ]);
    }
  }, [widgetSettings.welcomeMessage]);

  const { mutateAsync: createWidget, isPending: isCreating } = useMutation({
    mutationFn: (payload: Partial<ChatWidget>) =>
      chatIntegrationAPI.createChatWidget(payload),
    onSuccess: () => {
      toast.success('Widget created successfully!');
      queryClient.invalidateQueries({ queryKey: ['chatWidgets'] });
      navigate('/integration/chat-integration');
    },
    onError: (err: unknown) => {
      console.log(err);
      toast.error('Failed to create widget');
    },
  });

  const { mutateAsync: updateWidget, isPending: isUpdating } = useMutation({
    mutationFn: ({
      widgetId,
      payload,
    }: {
      widgetId: number;
      payload: Partial<ChatWidget>;
    }) => chatIntegrationAPI.updateChatWidget(widgetId, payload as ChatWidget),
    onSuccess: () => {
      toast.success('Widget updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['chatWidgets'] });
      queryClient.invalidateQueries({ queryKey: ['chatWidget', widgetId] });
      navigate('/integration/chat-integration');
    },
    onError: (err: unknown) => {
      console.log(err);
      toast.error('Failed to update widget');
    },
  });

  const handleSettingChange = (
    key: string,
    value: string | boolean | number
  ) => {
    setWidgetSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Generate embed code with real-time updates
  const embedCode = useMemo(() => {
    if (mode !== 'edit' || !existingWidget) return '';

    const config: Record<string, unknown> = {
      widgetKey: existingWidget.key,
      apiBase: 'https://admin-backend.findecor.io',
      socketUrl: 'wss://admin-backend.findecor.io',
    };

    // Add optional props only if they are not false/null/undefined
    if (widgetSettings.primaryColor) config.color = widgetSettings.primaryColor;
    if (widgetSettings.textColor) config.textColor = widgetSettings.textColor;
    if (widgetSettings.size) config.widgetSize = widgetSettings.size;
    if (widgetSettings.position)
      config.position =
        POSITION_MAP[widgetSettings.position] || widgetSettings.position;
    if (widgetSettings.borderRadius)
      config.borderRadius = `${widgetSettings.borderRadius}px`;
    if (widgetSettings.companyName)
      config.companyName = widgetSettings.companyName;
    if (widgetSettings.websiteUrl)
      config.websiteUrl = widgetSettings.websiteUrl;
    if (
      widgetSettings.autoOpen !== undefined &&
      widgetSettings.autoOpen !== false
    )
      config.autoOpen = widgetSettings.autoOpen;
    if (
      widgetSettings.autoDelay !== undefined &&
      widgetSettings.autoDelay !== false
    )
      config.autoDelay = widgetSettings.autoDelay;
    if (
      widgetSettings.showTypingIndicator !== undefined &&
      widgetSettings.showTypingIndicator !== false
    )
      config.typingIndicator = widgetSettings.showTypingIndicator;
    if (
      widgetSettings.enableSoundNotifications !== undefined &&
      widgetSettings.enableSoundNotifications !== false
    )
      config.soundNotifications = widgetSettings.enableSoundNotifications;
    if (widgetSettings.headerText)
      config.headerText = widgetSettings.headerText;
    if (widgetSettings.welcomeMessage)
      config.welcomeMessage = widgetSettings.welcomeMessage;
    if (widgetSettings.offlineMessage)
      config.offlineMessage = widgetSettings.offlineMessage;
    if (widgetSettings.placeholderText)
      config.inputPlaceholder = widgetSettings.placeholderText;
    if (widgetSettings.buttonText)
      config.buttonText = widgetSettings.buttonText;

    const configString = JSON.stringify(config, null, 2);

    return `<script src="https://cdn.jsdelivr.net/gh/Jobir03/test-widget@master/dist/findecor-chat-widget.umd.js"></script>
<script>
  window.FindecorChatWidget.init(${configString});
</script>`;
  }, [mode, existingWidget, widgetSettings]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success('Embed code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = () => {
    const payload: Partial<ChatWidget> = {
      color: widgetSettings.primaryColor,
      textColor: widgetSettings.textColor,
      widgetSize: widgetSettings.size as 'small' | 'medium' | 'large',
      position: POSITION_MAP[widgetSettings.position] as
        | 'BR'
        | 'TR'
        | 'BL'
        | 'TL',
      borderRadius: widgetSettings.borderRadius
        ? `${widgetSettings.borderRadius}px`
        : undefined,
      companyName: widgetSettings.companyName || undefined,
      websiteUrl: widgetSettings.websiteUrl || undefined,
      autoOpen: widgetSettings.autoOpen,
      autoDelay: widgetSettings.autoDelay,
      typingIndicator: widgetSettings.showTypingIndicator,
      soundNotifications: widgetSettings.enableSoundNotifications,
      headerText: widgetSettings.headerText || null,
      welcomeMessage: widgetSettings.welcomeMessage || null,
      offlineMessage: widgetSettings.offlineMessage || null,
      inputPlaceholder: widgetSettings.placeholderText || null,
      buttonText: widgetSettings.buttonText || null,
      isActive: mode === 'edit' ? (existingWidget?.isActive ?? true) : true,
    };

    if (mode === 'add') {
      createWidget(payload);
    } else if (widgetId) {
      updateWidget({ widgetId: parseInt(widgetId), payload });
    }
  };

  const handleReset = () => {
    setWidgetSettings({
      primaryColor: '#030213',
      textColor: '#ffffff',
      position: 'bottom-right',
      welcomeMessage: 'Hi! How can we help you today?',
      companyName: '',
      websiteUrl: '',
      showTypingIndicator: false,
      enableSoundNotifications: false,
      offlineMessage: '',
      placeholderText: '',
      headerText: '',
      buttonText: '',
      autoOpen: false,
      autoDelay: false,
      borderRadius: '8',
      size: 'medium',
    });
    toast.success('Settings reset to default');
  };

  const handlePreviewSend = () => {
    if (!previewInput.trim()) return;

    setPreviewMessages((prev) => [
      ...prev,
      { type: 'user', text: previewInput, time: 'Just now' },
    ]);

    setPreviewInput('');

    // Simulate bot response
    setTimeout(() => {
      setPreviewMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          text: 'Thanks for your message! Our team will respond shortly.',
          time: 'Just now',
        },
      ]);
    }, 1000);
  };

  const getPositionClasses = () => {
    switch (widgetSettings.position) {
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      default:
        return 'bottom-4 right-4';
    }
  };

  const getSizeClasses = () => {
    switch (widgetSettings.size) {
      case 'small':
        return 'w-80 h-96';
      case 'medium':
        return 'w-96 h-[500px]';
      case 'large':
        return 'w-[450px] h-[600px]';
      default:
        return 'w-96 h-[500px]';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:space-x-4">
          <div className="flex items-center">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Back</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-semibold">
              Web Chat Widget Customization
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              Customize your chat widget and get the embed code
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:justify-end">
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={isCreating || isUpdating || isLoadingWidget}
          >
            {isCreating || isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {mode === 'edit' ? 'Updating...' : 'Creating...'}
              </>
            ) : mode === 'edit' ? (
              'Save Changes'
            ) : (
              'Create Widget'
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="space-y-6">
          <Tabs defaultValue="appearance" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="appearance">Appearance</TabsTrigger>
              <TabsTrigger value="behavior">Behavior</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
            </TabsList>

            <TabsContent value="appearance" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Widget Appearance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex space-x-2">
                      <Input
                        type="color"
                        value={widgetSettings.primaryColor}
                        onChange={(e) =>
                          handleSettingChange('primaryColor', e.target.value)
                        }
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={widgetSettings.primaryColor}
                        onChange={(e) =>
                          handleSettingChange('primaryColor', e.target.value)
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Text Color</Label>
                    <div className="flex space-x-2">
                      <Input
                        type="color"
                        value={widgetSettings.textColor}
                        onChange={(e) =>
                          handleSettingChange('textColor', e.target.value)
                        }
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={widgetSettings.textColor}
                        onChange={(e) =>
                          handleSettingChange('textColor', e.target.value)
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Widget Position</Label>
                    <Select
                      value={widgetSettings.position}
                      onValueChange={(value) =>
                        handleSettingChange('position', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right">
                          Bottom Right (BR)
                        </SelectItem>
                        <SelectItem value="bottom-left">
                          Bottom Left (BL)
                        </SelectItem>
                        <SelectItem value="top-right">
                          Top Right (TR)
                        </SelectItem>
                        <SelectItem value="top-left">Top Left (TL)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Widget Size</Label>
                    <Select
                      value={widgetSettings.size}
                      onValueChange={(value) =>
                        handleSettingChange('size', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Border Radius (px)</Label>
                    <Input
                      type="number"
                      value={widgetSettings.borderRadius}
                      onChange={(e) =>
                        handleSettingChange('borderRadius', e.target.value)
                      }
                      min="0"
                      max="24"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={widgetSettings.companyName}
                      onChange={(e) =>
                        handleSettingChange('companyName', e.target.value)
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Website URL</Label>
                    <Input
                      type="url"
                      value={widgetSettings.websiteUrl}
                      onChange={(e) =>
                        handleSettingChange('websiteUrl', e.target.value)
                      }
                      placeholder="https://example.com"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="behavior" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Widget Behavior</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Auto Open</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically open chat on page load
                      </p>
                    </div>
                    <Switch
                      checked={widgetSettings.autoOpen}
                      onCheckedChange={(checked) =>
                        handleSettingChange('autoOpen', checked)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Auto Delay</Label>
                      <p className="text-sm text-muted-foreground">
                        Enable auto delay for opening
                      </p>
                    </div>
                    <Switch
                      checked={widgetSettings.autoDelay}
                      onCheckedChange={(checked) =>
                        handleSettingChange('autoDelay', checked)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Typing Indicator</Label>
                      <p className="text-sm text-muted-foreground">
                        Show when agent is typing
                      </p>
                    </div>
                    <Switch
                      checked={widgetSettings.showTypingIndicator}
                      onCheckedChange={(checked) =>
                        handleSettingChange('showTypingIndicator', checked)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Sound Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Play sound for new messages
                      </p>
                    </div>
                    <Switch
                      checked={widgetSettings.enableSoundNotifications}
                      onCheckedChange={(checked) =>
                        handleSettingChange('enableSoundNotifications', checked)
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="messages" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Message Customization</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Header Text</Label>
                    <Input
                      value={widgetSettings.headerText}
                      onChange={(e) =>
                        handleSettingChange('headerText', e.target.value)
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Welcome Message</Label>
                    <Textarea
                      value={widgetSettings.welcomeMessage}
                      onChange={(e) =>
                        handleSettingChange('welcomeMessage', e.target.value)
                      }
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Offline Message</Label>
                    <Textarea
                      value={widgetSettings.offlineMessage}
                      onChange={(e) =>
                        handleSettingChange('offlineMessage', e.target.value)
                      }
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Input Placeholder</Label>
                    <Input
                      value={widgetSettings.placeholderText}
                      onChange={(e) =>
                        handleSettingChange('placeholderText', e.target.value)
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Button Text</Label>
                    <Input
                      value={widgetSettings.buttonText}
                      onChange={(e) =>
                        handleSettingChange('buttonText', e.target.value)
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Embed Code Section */}
          {mode === 'edit' ? (
            <Card>
              <CardHeader>
                <CardTitle>Embed Code</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Copy and paste this code before the closing &lt;/body&gt; tag
                  of your website.
                </p>
                <div className="relative">
                  <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{embedCode}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={handleCopyCode}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Code
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>

        {/* Live Preview Panel */}
        <div className="space-y-4">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>Live Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-8 min-h-[600px] relative">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  This is how your chat widget will appear on your website
                </p>

                {/* Chat Widget Preview */}
                <div className={`fixed ${getPositionClasses()}`}>
                  {!previewOpen ? (
                    <button
                      onClick={() => setPreviewOpen(true)}
                      className="shadow-lg rounded-full p-4 transition-transform hover:scale-110"
                      style={{ backgroundColor: widgetSettings.primaryColor }}
                    >
                      <MessageCircle
                        className="h-6 w-6"
                        style={{ color: widgetSettings.textColor }}
                      />
                    </button>
                  ) : (
                    <div
                      className={`${getSizeClasses()} shadow-2xl flex flex-col bg-white`}
                      style={{
                        borderRadius: `${widgetSettings.borderRadius}px`,
                      }}
                    >
                      {/* Header */}
                      <div
                        className="p-4 flex items-center justify-between"
                        style={{
                          backgroundColor: widgetSettings.primaryColor,
                          borderTopLeftRadius: `${widgetSettings.borderRadius}px`,
                          borderTopRightRadius: `${widgetSettings.borderRadius}px`,
                        }}
                      >
                        <div className="flex items-center space-x-2">
                          <div>
                            <p
                              className="font-medium text-sm"
                              style={{ color: widgetSettings.textColor }}
                            >
                              {widgetSettings.headerText}
                            </p>
                            <p
                              className="text-xs opacity-80"
                              style={{ color: widgetSettings.textColor }}
                            >
                              {widgetSettings.companyName}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setPreviewOpen(false)}
                          className="p-1 rounded hover:opacity-80"
                          style={{ transition: 'opacity 0.2s' }}
                        >
                          <X
                            className="h-4 w-4"
                            style={{ color: widgetSettings.textColor }}
                          />
                        </button>
                      </div>

                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                        {previewMessages.map((message, index) => (
                          <div
                            key={index}
                            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : ''}`}
                            >
                              <div
                                className="rounded-lg p-3 text-sm"
                                style={{
                                  backgroundColor:
                                    message.type === 'user'
                                      ? widgetSettings.primaryColor
                                      : '#e5e7eb',
                                  color:
                                    message.type === 'user'
                                      ? widgetSettings.textColor
                                      : '#000000',
                                }}
                              >
                                {message.text}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {message.time}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Input */}
                      <div
                        className="p-4 border-t bg-white"
                        style={{
                          borderBottomLeftRadius: `${widgetSettings.borderRadius}px`,
                          borderBottomRightRadius: `${widgetSettings.borderRadius}px`,
                        }}
                      >
                        <div className="flex space-x-2">
                          <Input
                            value={previewInput}
                            onChange={(e) => setPreviewInput(e.target.value)}
                            placeholder={widgetSettings.placeholderText}
                            onKeyPress={(e) =>
                              e.key === 'Enter' && handlePreviewSend()
                            }
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            onClick={handlePreviewSend}
                            style={{
                              backgroundColor: widgetSettings.primaryColor,
                            }}
                          >
                            <Send
                              className="h-4 w-4"
                              style={{ color: widgetSettings.textColor }}
                            />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
